import { type FormEvent, useEffect, useState } from "react";
import { apiFetch } from "@/lib/api/client";

export type AuthState = "checking" | "authenticated" | "anonymous";

export function useAuth() {
  const [authState, setAuthState] = useState<AuthState>("checking");
  const [authUser, setAuthUser] = useState("");
  const [loginDraft, setLoginDraft] = useState({ username: "", password: "" });
  const [loginPending, setLoginPending] = useState(false);
  const [loginError, setLoginError] = useState("");

  useEffect(() => {
    let active = true;
    apiFetch("/api/auth/me")
      .then(async (response) => {
        const payload = await response.json().catch(() => ({}));
        if (!active) return;
        if (response.status === 404) {
          setAuthState("authenticated");
        } else if (response.ok && payload.ok) {
          setAuthUser(payload.user?.username || "");
          setAuthState("authenticated");
        } else {
          setAuthState("anonymous");
        }
      })
      .catch(() => active && setAuthState("anonymous"));
    return () => { active = false; };
  }, []);

  const login = async (event: FormEvent) => {
    event.preventDefault();
    setLoginPending(true);
    setLoginError("");
    try {
      const response = await apiFetch("/api/auth/login", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(loginDraft),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload.ok) throw new Error(payload.error || `HTTP ${response.status}`);
      setAuthUser(payload.user?.username || loginDraft.username);
      setAuthState("authenticated");
      setLoginDraft({ username: "", password: "" });
    } catch (error) {
      setLoginError(String(error instanceof Error ? error.message : error));
    } finally {
      setLoginPending(false);
    }
  };

  const logout = async () => {
    await apiFetch("/api/auth/logout", { method: "POST" }).catch(() => undefined);
    setAuthUser("");
    setAuthState("anonymous");
  };

  return { authState, authUser, loginDraft, loginPending, loginError, setLoginDraft, login, logout };
}
